import { Settings } from './../../core/settings';
import { SponsorRouteData } from './../sponsor-route-data';
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, of, merge } from 'rxjs';
import { map, flatMap, tap } from 'rxjs/operators';
import { comparators, UrlBuilder } from '../../core';
import { BarParams, BarRendererComponent, CovanceGridColumn, dateRenderer, dateTimeComparator, LinkAgRendererComponent, LinkParams } from '../../covance-ag-grid';
import { SponsorSubject } from '../sponsor-subject';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { SponsorSubjectsStorage } from '../sponsor-subjects-storage';
import { routeData } from '../../shared/route-helpers';
import { SponsorStudySiteIdentity } from '../sites/sponsor-study-site-identity';

export interface StudySubjectParams {
    data: SponsorSubject;
}

function getNumberAndInitials(s: SponsorSubject): string {
    return SubjectTitleRenderer.renderTitle({
        data: {
            subjectNumber: s.subjectNumber,
            initials: s.subjectInitials,
            screeningNumber: s.screeningNumber,
            zcSubjectId: s.zcSubjectId,
        }
    });
}

@Component({
    selector: 'study-subjects',
    template: require('./study-subjects.component.html'),
    styles: [require('./study-subjects.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StudySubjectsComponent {
    public readonly subjectsData$: Observable<SponsorSubject[]>;
    public readonly gridOptions: GridOptions;
    public readonly columnDefs: CovanceGridColumn[];
    public readonly siteIdentities$: Observable<SponsorStudySiteIdentity[]>;
    public readonly isLargeStudy: boolean;
    public readonly showMessage$: Observable<boolean>;
    private readonly selectedSiteIdentities$: BehaviorSubject<SponsorStudySiteIdentity[]>;

    private maxNumOfAlertsSnapshot: number;

    constructor(
        private readonly sponsorRouteData: SponsorRouteData,
        private readonly sponsorSubjectsStorage: SponsorSubjectsStorage,
        private urlBuilder: UrlBuilder,
        private route: ActivatedRoute,
        settings: Settings
    ) {
        this.siteIdentities$ = this.route.data.pipe(map(x => x[routeData.studySiteIdentities] as SponsorStudySiteIdentity[]));
        this.selectedSiteIdentities$ = new BehaviorSubject<SponsorStudySiteIdentity[]>([]);
        this.showMessage$ = this.selectedSiteIdentities$.pipe(map(s => s.length === 0 && this.isLargeStudy));
        this.isLargeStudy = this.sponsorRouteData.snapshot.selectedStudy.numberOfSubjects > settings.extremelyLargeSiteThreshhold;
        this.subjectsData$ = (this.isLargeStudy ? this.loadSubjectsBySite() : this.loadAllSubjects())
            .pipe(tap(subjects => this.maxNumOfAlertsSnapshot = Math.max(...(subjects || []).map(s => s.alertsCount))));

        this.columnDefs = this.getGridColumns();

        this.gridOptions = { rowHeight: 27 };
    }

    public onSelectedSiteIdentityChanged($event: SponsorStudySiteIdentity[]) {
        this.selectedSiteIdentities$.next($event);
    }


    private loadAllSubjects() {
        return this.sponsorSubjectsStorage.loadSubjectsByStudy(this.sponsorRouteData.selectedStudy)
            .pipe(
                flatMap(subjects => this.selectedSiteIdentities$
                    .pipe(
                        map(selectedSites =>
                            isEmpty(selectedSites)
                                ? subjects
                                : (subjects || []).filter(s => selectedSites.some(selSite => s.siteId === selSite.siteId))))));
    }

    private loadSubjectsBySite() {
        return merge(of([]), this.sponsorSubjectsStorage.loadSubjectsByStudySiteIdentities(this.sponsorRouteData.selectedStudy, this.selectedSiteIdentities$));
    }

    private getGridColumns(): CovanceGridColumn[] {

        const columns: CovanceGridColumn[] = [
            {
                headerName: 'Investigator',
                field: 'investigatorFullName',
                comparator: (a, b) => comparators.mixedStringComparator(a, b),
            },
            {
                headerName: 'Subject',
                field: 'subjectNumber',
                valueGetter: (params: StudySubjectParams): LinkParams => {
                    if (!params.data) {
                        return null;
                    }

                    const selectedStudy = this.sponsorRouteData.snapshot.selectedStudy;
                    const subjectUrl = params.data.subjectId
                        ? this.urlBuilder.esa().sponsor().study(selectedStudy).subjects(params.data.zcSubjectId).url()
                        : [];

                    const subjectNumAndInitials = getNumberAndInitials(params.data);

                    return {
                        routerLink: subjectUrl,
                        text: subjectNumAndInitials
                    };
                },
                filterValueGetter: (params: StudySubjectParams) => getNumberAndInitials(params.data),
                comparator: comparators.linkParamsComparator,
                cellRendererFramework: LinkAgRendererComponent
            },
            {
                headerName: 'Site',
                field: 'siteNumber',
                filter: 'agSetColumnFilter',
                comparator: (a, b) => comparators.mixedStringComparator(a, b),
            },
            {
                headerName: 'Gender',
                field: 'gender',
                valueGetter: (params: StudySubjectParams) => params.data.gender === 'F' ? 'Female' :
                    params.data.gender === 'M' ? 'Male' :
                        '',
            },
            {
                headerName: 'Age',
                field: 'age',
                valueGetter: (params: StudySubjectParams) => params.data.isAgeProject === false ? params.data.age :
                    params.data.isAgeProject === true ? params.data.ageProjectDisplayValue :
                        '',
            },
            {
                headerName: 'Last Visit',
                field: 'lastVisitDescription',
                comparator: (a, b) => comparators.mixedStringComparator(a, b),
            },
            {
                headerName: 'Last Visit Date',
                field: 'lastVisitDate',
                cellRenderer: dateRenderer,
                comparator: dateTimeComparator,
                filterValueGetter: (params: StudySubjectParams) => dateRenderer({value: params.data.lastVisitDate}),
            },
            {
                headerName: 'Result Alerts',
                filter: 'agNumberColumnFilter',
                field: 'alertsCount',
                cellRendererFramework: BarRendererComponent,
                valueGetter: (params: StudySubjectParams): BarParams => {
                    return {
                        threshold: this.maxNumOfAlertsSnapshot,
                        value: params.data.alertsCount
                    };
                },
                filterValueGetter: (params: StudySubjectParams) => params.data.alertsCount,
                comparator: (a: BarParams, b: BarParams) => {
                    return comparators.numberComparator(a ? a.value : null, b ? b.value : null);
                }
            }
        ];
        return columns;
    }
}
